import React from 'react';
import * as mobx from 'mobx';
import { FormattedMessage, IntlShape } from 'react-intl';
import {
    AttendeeAttendanceMethod,
    AttendeeRegistrationStatus,
    IAttendeeDetailsDTO,
    IAttendeeOrganizationDTO,
    PodiumQuestionAnswerPriorityType
} from '../../../../../api/attendees.dao';
import { LONG_RICH_TEXT_LENGTH, MAX_ACCESS_CODE_LENGTH, MIN_ACCESS_CODE_LENGTH, SHORT_PLAIN_TEXT_LENGTH } from '../../../../../constants/common.constants';
import { EMAIL_VALIDATION_REG_EXP } from '../../../../../constants/regex.constants';
import { calcByteCodeSize } from '../../../../../infrastructure/utils/common.utils';
import UploadImageS3VM from '../../shared/upload-image/upload-image-to-s3.viewmodel';
import { FIELD_VALIDATION_PRIORITY_ERROR_MESSAGE } from '../../../../../constants/validation-errors';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../global/rich-text-editor/utils/transpiler';

// Any manipulations on data items shall be performed here
// if something can not be set directly - make it private with public getter
class AttendeeFormDataViewModel {
    private attendeeId: number;

    @mobx.observable
    private podiumQuestionAnswerPriorityType: PodiumQuestionAnswerPriorityType | null;
    @mobx.observable
    public externalAttendeeId: string;
    @mobx.observable
    public title: string;
    @mobx.observable
    public firstName: string;
    @mobx.observable
    public lastName: string;
    @mobx.observable
    public email: string;
    @mobx.observable
    public assistantEmail: string;
    @mobx.observable
    public podiumQaNote: string;
    @mobx.observable
    public phoneNumber: string;
    @mobx.observable
    public accessCode: string;
    @mobx.observable
    public organizationId: number;
    @mobx.observable
    public salutation: string;
    @mobx.observable
    public emailNotProvided: boolean;
    @mobx.observable
    private organizations: IAttendeeOrganizationDTO[];
    @mobx.observable
    public priorityInOrganization: number;
    @mobx.observable
    public isMailTouched: boolean;
    @mobx.observable
    public speaker: boolean;
    @mobx.observable
    public biography: string;
    @mobx.observable
    public registrationStatus: AttendeeRegistrationStatus;
    public initialRegistrationStatus: AttendeeRegistrationStatus;
    @mobx.observable
    public sendRegistrationEmail: boolean = true;
    @mobx.observable
    public attendanceMethod: AttendeeAttendanceMethod | string;
    @mobx.observable
    public uploadProfileImageS3VM: UploadImageS3VM = null;

    intl: IntlShape;

    constructor(
        organizationsList: IAttendeeOrganizationDTO[],
        intl: IntlShape,
        private readonly accountId: number,
        private readonly eventId: number,
        attendeeDetails?: IAttendeeDetailsDTO,
        organizationId?: number,
    ) {
        this.organizations = organizationsList;
        this.emailNotProvided = false;
        this.priorityInOrganization = null;
        this.speaker = false;
        this.intl = intl;
        this.registrationStatus = AttendeeRegistrationStatus.APPROVED;
        this.uploadProfileImageS3VM = new UploadImageS3VM(this.accountId, this.eventId, 'profileImage', 'attendee');
        this.podiumQuestionAnswerPriorityType = null;

        if (attendeeDetails) {
            this.title = attendeeDetails.title;
            this.firstName = attendeeDetails.firstName;
            this.lastName = attendeeDetails.lastName;
            this.email = attendeeDetails.email;
            this.assistantEmail = attendeeDetails.assistantEmail || '';
            this.podiumQaNote = attendeeDetails.podiumQaNote || '';
            this.phoneNumber = attendeeDetails.phoneNumber;
            this.accessCode = attendeeDetails.accessCode;
            this.organizationId = attendeeDetails.organizationId;
            this.attendeeId = attendeeDetails.attendeeId;
            this.externalAttendeeId = attendeeDetails.externalAttendeeId;
            this.salutation = attendeeDetails.salutation;
            this.emailNotProvided = attendeeDetails.emailNotProvided;
            this.priorityInOrganization = attendeeDetails.priorityInOrganization;
            this.speaker = attendeeDetails.speaker;
            // Since CKEditor works with classes, but data is stored with inline styles,
            // all CKEditor values need to be converted to the appropriate format
            this.biography = transpileInlineStylesToClasses(attendeeDetails.biography);
            this.registrationStatus = attendeeDetails.registrationStatus;
            this.initialRegistrationStatus = attendeeDetails.registrationStatus;
            this.attendanceMethod = attendeeDetails.attendanceMethod || '';

            this.uploadProfileImageS3VM.addImage(
                attendeeDetails?.profileImageWithUrl?.assetUrl,
                attendeeDetails?.profileImage?.title,
                attendeeDetails?.profileImage?.objectKey
            );

            this.podiumQuestionAnswerPriorityType = attendeeDetails.podiumQuestionAnswerPriorityType;
        }
        if (organizationId) {
            this.organizationId = organizationId;
        }
    }

    public getOrganizations(): IAttendeeOrganizationDTO[] {
        return this.organizations;
    }

    @mobx.action
    public updateOrganizationsList = (newOrganizationsList: IAttendeeOrganizationDTO[]) => {
        this.organizations = newOrganizationsList;
    };

    @mobx.action
    public setOrganizationId = (newOrganizationId: number) => {
        this.organizationId = newOrganizationId;
    };

    @mobx.action
    public setPriorityInOrganization = (priorityInOrganization: number) => {
        this.priorityInOrganization = priorityInOrganization;
    };

    public checkForm() {
        return !this.getLastNameError()
            && !this.getFirstNameError()
            && !this.getEmailError(this.email, true)
            && !this.getEmailError(this.assistantEmail)
            && !this.getPodiumQaNoteError()
            && !this.getOrganizationError()
            && !this.getAccessCodeError()
            && !this.getSalutationError()
            && !this.getTitleError()
            && !this.getPhoneNumberError()
            && !this.getExternalAttendeeIDError()
            && !this.getPriorityInOrganizationError()
            && !this.getBiographyError(this.intl);
    }

    public getFirstNameError = () => {
        if (this.firstName) {
            if (this.firstName.length > SHORT_PLAIN_TEXT_LENGTH) {
                return <FormattedMessage
                    defaultMessage='Too long - max length is 255 characters'
                    id='oe.firstName.tooLong'
                    description='Error message text'
                />;
            }
            return null;
        }
        return <FormattedMessage
            defaultMessage='First Name is required'
            id='oe.user.userFirstNameIsRequired'
            description='Error message in user popover'
        />;
    };

    public getLastNameError = (): JSX.Element | null => {
        if (this.lastName) {
            if (this.lastName.length > SHORT_PLAIN_TEXT_LENGTH) {
                return <FormattedMessage
                    id='oe.lastName.tooLong'
                    defaultMessage='Too long - max length is 255 characters'
                    description='Error message text'
                />;
            }
            return null;
        }
        return <FormattedMessage
            defaultMessage='Last Name is required'
            id='oe.user.userLastNameIsRequired'
            description='Error message in user popover'
        />;
    };

    public getSalutationError = (): JSX.Element | null => {
        if (this.salutation && this.salutation.length > SHORT_PLAIN_TEXT_LENGTH) {
            return <FormattedMessage
                defaultMessage='Salutation is too long'
                id='oe.salutation.tooLong'
                description='Error message text'
            />;
        }
        return null;
    };

    public getTitleError = (): JSX.Element | null => {
        if (this.title && this.title.length > SHORT_PLAIN_TEXT_LENGTH) {
            return <FormattedMessage
                defaultMessage='Title is too long'
                id='oe.title.tooLong'
                description='Error message text'
            />;
        }
        return null;
    };

    public getPodiumQaNoteError = (): JSX.Element | null => {
        if (this.podiumQaNote && this.podiumQaNote.length > SHORT_PLAIN_TEXT_LENGTH) {
            return <FormattedMessage
                defaultMessage='Q&A Notes is too long'
                id='oe.qaNotes.tooLong'
                description='Error message text'
            />;
        }
        return null;
    };

    public getPhoneNumberError = (): JSX.Element | null => {
        if (this.phoneNumber && this.phoneNumber.length > SHORT_PLAIN_TEXT_LENGTH) {
            return <FormattedMessage
                defaultMessage='Phone number is too long'
                id='oe.phoneNumber.tooLong'
                description='Error message text'
            />;
        }
        return null;
    };

    public getOrganizationError = () => {
        return !this.organizationId && 'Organization is required';
    };

    public getAccessCodeError = () => {
        if (this.accessCode) {
            if (this.accessCode?.trim()?.length < MIN_ACCESS_CODE_LENGTH || this.accessCode?.length > MAX_ACCESS_CODE_LENGTH) {
                return `Access code length must be between ${MIN_ACCESS_CODE_LENGTH} and ${MAX_ACCESS_CODE_LENGTH} characters`;
            }
        }
        return null;
    };

    public getEmailError = (value: string, isRequired = false) => {
        if (value) {
            return EMAIL_VALIDATION_REG_EXP.test(value) ? null
                : <FormattedMessage
                    id='oe.email.emailIsNotValid'
                    defaultMessage='Must be a valid email address'
                    description='Error message in user popover'
                />;
        }
        if (isRequired) return <FormattedMessage
            id='oe.email.emailIsRequired'
            defaultMessage='Email is required'
            description='Error message in user popover'
        />;
    };

    public getExternalAttendeeIDError = () => {
        return this.externalAttendeeId?.length > SHORT_PLAIN_TEXT_LENGTH ? `External Id is too long` : null;

    };

    public getPriorityInOrganizationError = (): JSX.Element | null => {
        if (this.priorityInOrganization) {
            if (this.priorityInOrganization < 1 || this.priorityInOrganization > 99) {
                return FIELD_VALIDATION_PRIORITY_ERROR_MESSAGE(1, 99);
            }
        }
        return null;
    };

    @mobx.action
    public toggleSpeakerValue = (): void => {
        this.speaker = !this.speaker;
    };

    @mobx.action
    public toggleSendRegistrationEmail = (): void => {
        this.sendRegistrationEmail = !this.sendRegistrationEmail;
    };

    @mobx.action
    public setBiography(value: string): void {
        this.biography = value;
    }

    public getBiographyError(intl: IntlShape): string | null {
        return calcByteCodeSize(this.biography) > LONG_RICH_TEXT_LENGTH ?
            intl.formatMessage({
                description: 'Warning message',
                defaultMessage: 'Biography is too long',
                id: 'oe.attendee.biographyTooLong',
            }) : null;
    }

    public isImageUploading = (): boolean => {
        return this.uploadProfileImageS3VM.isImageUploading;
    };

    public isAttendeeNewRegistrationStatus(): boolean {
        return this.attendeeId && (this.initialRegistrationStatus !== this.registrationStatus);
    }

    public getPodiumQAPriorityType(): PodiumQuestionAnswerPriorityType | '' {
        return this.podiumQuestionAnswerPriorityType || '';
    }

    @mobx.action
    public setPodiumQAPriorityType(type: PodiumQuestionAnswerPriorityType): void {
        this.podiumQuestionAnswerPriorityType = type || null;
    }

    public toUpdateAPIDTO(): IAttendeeDetailsDTO {
        const profileImage = this.uploadProfileImageS3VM.getImageToApi();
        return {
            title: this.title?.trim() || null,
            firstName: this.firstName?.trim(),
            lastName: this.lastName?.trim(),
            email: this.email?.trim(),
            assistantEmail: this.assistantEmail?.trim() || null,
            phoneNumber: this.phoneNumber?.trim(),
            accessCode: this.accessCode?.trim() || null,
            organizationId: this.organizationId,
            attendeeId: this.attendeeId,
            externalAttendeeId: this.externalAttendeeId?.trim() || null,
            salutation: this.salutation?.trim() || null,
            emailNotProvided: this.emailNotProvided,
            priorityInOrganization: this.priorityInOrganization || null,
            speaker: this.speaker,
            // Since CKEditor works with classes, but data is stored with inline styles,
            // all CKEditor values need to be converted to the appropriate format
            biography: transpileClassesToInlineStyles(this.biography),
            registrationStatus: this.registrationStatus,
            attendanceMethod: this.attendanceMethod || null,
            podiumQuestionAnswerPriorityType: this.podiumQuestionAnswerPriorityType,
            podiumQaNote: this.podiumQaNote,
            ...profileImage
        };
    }
}

export { AttendeeFormDataViewModel };
